import React, { useState, useEffect } from 'react'
import CommentEditor from './CommentEditor'
import CommentWithReplies from './CommentWithReplies'
import CommentAuthor from './CommentAuthor'
import styled from 'styled-components'
import SortableList from 'components_v1/sortableList/SortableList'
import { Flex } from 'ui/acro'
import { H2 } from 'ui/bend'
import commentAxios from 'api/commentsAxios'
import { LightTheme } from 'ui/bend'
import { ThemeProvider } from 'styled-components'
import { Spinner } from 'ui/bend'
import Snackbar from './ReportSuccessSnakbar'
import { useWindowResize } from 'hooks'
import { class_series_comment_events } from 'gtm/pushEvents'

const CommentTitle = styled(H2)`
  color: #000;
  font-family: 'Proxima-Nova';
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  margin-top: 32px;
`
const LoadMore = styled.div`
  padding: 12px 32px;
  border: 1px solid #000;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px; /* 133.333% */
  letter-spacing: 0.9px;
  text-transform: uppercase;
  width: 180px;
  cursor: pointer;
`
const Pagination = styled(Flex)`
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.352px;
  color: #000;
  font-family: 'Proxima-Nova';
  font-style: normal;
`

const CommentBoxContainer = styled(Flex)`
  padding: 32px 0 8px 0;
  border-bottom: ${(props) =>
    props.count > 0 ? '1px solid #ccc' : '0px solid #ccc'};
  margin-bottom: 32px;
  gap: 24px;
  align-items: flex-start;

  @media (max-width: 768px) {
    border-top: 1px solid #ccc;
    padding: 24px 0 0 0;
    gap: 12px;
  }
`
const BoldText = styled.div`
  font-weight: 600;
`
const SpinnerContainer = styled.div`
  margin-top: 64px;
  margin-bottom: 32px;
`

const CommentCountContainer = styled(Flex)`
  gap: 24px;
  @media (max-width: 768px) {
    gap: 12px;
  }
  #reload-comments {
    display: none;
  }

  #comment-sort {
    div {
      div {
        ul {
          left: 0 !important;
          transform: translateX(0%);
        }
      }
    }
  }
`

// Main Component
const CommentSection = ({
  type,
  classDetails,
  user = {},
  coaches,
  videoDetails,
  seriesDetails
}) => {
  const [comments, setComments] = useState([])
  const [order, setOrder] = useState('date')
  const [editingId, setEditingId] = useState(null)
  const [activeReplyId, setActiveReplyId] = useState(null)
  const [commentCount, setCommentCount] = useState(null)
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null)
  const [loading, setLoading] = useState(true)
  const [load, setLoad] = useState(false)
  const [showSnackbar, setShowSnackbar] = useState(false)
  const { width } = useWindowResize()
  const sortOptions = ['date', 'oldest', 'liked']

  const { firstName, lastName, profilePhoto } = user || {}
  const fullName = `${firstName} ${lastName}`

  useEffect(() => {
    fetchComments(null)
  }, [order])

  const onSortDropDownChange = (sortOption) => {
    if (order !== sortOption) {
      setOrder(sortOption)
      setLastEvaluatedKey(null)
    }
  }

  const getSortOrder = () => {
    let cmtOrder = 'newest'
    switch (order) {
      case 'date':
        cmtOrder = 'newest'
        break
      case 'oldest':
        cmtOrder = 'oldest'
        break
      case 'liked':
        cmtOrder = 'top'
        break
      default:
        cmtOrder = 'newest'
    }

    return cmtOrder
  }

  const invokeGA4Event = (eventType) => {
    if (type === 'CLASS') {
      class_series_comment_events(
        type.toLowerCase(),
        eventType,
        seriesDetails?.title,
        coaches?.map((coach) => coach?.fullName).join(', '),
        classDetails?.title,
        videoDetails?.title
      )
    } else if (type === 'SERIES') {
      class_series_comment_events(
        type.toLowerCase(),
        eventType,
        classDetails?.title,
        coaches?.map((coach) => coach?.fullName).join(', ')
      )
    }
  }

  const fetchComments = async (lastEvaluatedItem) => {
    try {
      setLoading(true)
      const response = await commentAxios.post('/comments', {
        content_type: type,
        content_id: classDetails?.id,
        limit: 20,
        sort: getSortOrder(),
        last_evaluated_key: lastEvaluatedItem
      })
      if (response.status === 200) {
        if (lastEvaluatedItem) {
          setComments((prevComments) =>
            prevComments?.concat(response?.data?.comments)
          )
        } else {
          setComments(response?.data?.comments)
        }

        setCommentCount(response?.data?.commentCount)
        setLastEvaluatedKey(response?.data?.lastEvaluatedKey)
        sessionStorage.setItem('commentCount', response?.data?.commentCount)
        window.dispatchEvent(new Event('session-storage-changed'))
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  const addComment = async (text) => {
    if (load) return
    try {
      setLoad(true)
      const response = await commentAxios.post('/content-interaction', {
        content_type: type,
        content_id: classDetails?.id,
        text: text,
        title: classDetails?.title,
        instructors: coaches?.map((coach) => ({
          ...coach,
          full_name: coach?.fullName
        }))
      })
      if (response.status === 200) {
        invokeGA4Event('post')
        setLastEvaluatedKey(null)
        fetchComments(null)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoad(false)
    }
  }

  const addReply = async (commentId, reply) => {
    if (load) return
    try {
      setLoad(true)
      const response = await commentAxios.post('/content-interaction', {
        content_type: type,
        content_id: classDetails?.id,
        text: reply,
        comment_id: commentId
      })
      if (response.status === 200) {
        invokeGA4Event('reply')
        setComments((prev) => {
          return prev?.map((comment) => {
            return comment.commentId === commentId
              ? {
                  ...comment,
                  replyCount: comment.replyCount + 1
                }
              : comment
          })
        })
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoad(false)
    }
  }

  const updateComment = async (updatedText) => {
    if (load) return
    try {
      setLoad(true)
      const response = await commentAxios.put('/content-interaction', {
        content_type: type,
        content_id: classDetails?.id,
        text: updatedText,
        comment_id: editingId
      })
      if (response.status === 200) {
        setComments((prev) =>
          prev.map((comment) =>
            comment.commentId === editingId
              ? { ...comment, text: updatedText }
              : comment
          )
        )
        setEditingId(null)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoad(false)
    }
  }

  const deleteComment = async (id) => {
    if (load) return
    try {
      setLoad(true)
      const response = await commentAxios.post('/content-interaction/delete', {
        content_type: type,
        content_id: classDetails?.id,
        comment_id: id
      })
      if (response.status === 200) {
        if (editingId === id) setEditingId(null)
        setLastEvaluatedKey(null)
        fetchComments(null)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoad(false)
    }
  }

  const incrementPageNumber = () => {
    lastEvaluatedKey && fetchComments(lastEvaluatedKey)
  }

  const handleReload = () => {
    setLastEvaluatedKey(null)
    fetchComments(null)
  }

  return (
    <ThemeProvider theme={LightTheme}>
      {typeof commentCount === 'number' && (
        <Flex
          flexDirection='column'
          width={['90%', '75%', '75%', '75%']}
          mt='0'
          mb='0'
          ml='auto'
          mr='auto'
        >
          <CommentCountContainer
            flexDirection={['column', 'row', 'row', 'row']}
            justifyContent='space-between'
            style={{ margin: '32px 0', alignItems: 'baseline' }}
          >
            <CommentTitle>
              {commentCount} {type === 'SERIES' ? 'PROGRAMS' : type} Comment
              {commentCount === 1 ? '' : 's'}
            </CommentTitle>
            <div id='comment-sort'>
              <SortableList
                sortableFields={sortOptions}
                order={order}
                onSort={onSortDropDownChange}
                isMobile={width <= 1024}
              />
              <button id='reload-comments' onClick={handleReload} />
            </div>
          </CommentCountContainer>
          {user?.username && (
            <CommentBoxContainer
              flexDirection={['column', 'row', 'row', 'row']}
              justifyContent='space-between'
              count={commentCount}
            >
              <CommentAuthor
                user={{ avatar: profilePhoto, fullName }}
                style={{ width: '20%' }}
              />
              <Flex width={['100%', '85%', '75%', '75%']}>
                <CommentEditor mode='new' onSubmit={addComment} />
              </Flex>
            </CommentBoxContainer>
          )}

          {comments?.map((comment) => (
            <CommentWithReplies
              key={comment.commentId}
              comment={comment}
              isEditing={editingId === comment.commentId}
              onEdit={(id) => {
                setEditingId(id)
                setActiveReplyId(null) // cancel reply if editing
              }}
              onSaveEdit={updateComment}
              onCancelEdit={() => setEditingId(null)}
              onDelete={deleteComment}
              onReply={addReply}
              activeReplyId={activeReplyId}
              setActiveReplyId={setActiveReplyId}
              type={type}
              id={classDetails?.id}
              userName={user?.username}
              setComments={setComments}
              setShowSnackbar={setShowSnackbar}
              user={{ avatar: profilePhoto, fullName }}
              invokeGA4Event={invokeGA4Event}
            />
          ))}
          {lastEvaluatedKey && !loading && (
            <Flex alignItems='center' flexDirection='column'>
              <LoadMore onClick={() => incrementPageNumber()}>
                Load more
              </LoadMore>
            </Flex>
          )}
          {commentCount > 0 && !loading && (
            <Flex alignItems='center' flexDirection='column'>
              <Pagination>
                Viewing &nbsp;<BoldText>1</BoldText>&nbsp;-&nbsp;
                <BoldText>{comments?.length}</BoldText>
                &nbsp;of <BoldText>&nbsp;{commentCount}</BoldText>
              </Pagination>
            </Flex>
          )}
          {loading && (
            <SpinnerContainer>
              <Spinner />
            </SpinnerContainer>
          )}
          <Snackbar
            message='Thanks for the report! Our moderators will review it shortly.'
            show={showSnackbar}
            onClose={() => setShowSnackbar(false)}
          />
        </Flex>
      )}
    </ThemeProvider>
  )
}

export default CommentSection
